import {
  AGE_GROUPS,
  EVENT_STATUS,
  INDUSTRY_TYPES,
  LANGUAGES,
  SKILL_LEVELS,
  SKILLS,
} from "utils/helpers/enums/shared.enum";
import { HostProfileDTO } from "../host-dtos/host-profile.dto";
import { PictureDTO } from "./picture.dto";
import { format, isFuture, parseISO } from "date-fns";
import { UserDTO } from "./user.dto";
import { BadgeDTO } from "../host-dtos/badge.dto";
import { MinorDTO } from "../attendee/minor.dto";

export class EventDTO {
  id: number;
  title: string = "";
  description: string = "";
  location: string = "";
  ageGroup: AGE_GROUPS;
  joineeSlots: number;
  badge: BadgeDTO;
  skillLevel: SKILL_LEVELS = SKILL_LEVELS.EXCEPTIONAL_LEARNER;
  coreConcept: number[] = [];
  coreConceptIds: number[] = [];
  parentSupervision: boolean = true;
  language: LANGUAGES;
  physicalConsideration: string = "";
  specialInstructions: string = "";
  ambassadorSupportRequired: boolean = false;
  rsvpEmail: string = "";
  badgeUrlId: number;
  badgeUrl: any;
  entryFee: number = 0;
  rsvpId: any;
  ambassadorJoiningRequired: boolean = false;
  subscriptionCount: number = 0;
  city: string = "";
  state: string = "";
  industryAndInterests: INDUSTRY_TYPES;
  isSession: boolean = true;
  hostId: number;
  eventStatus: EVENT_STATUS;
  deleted: boolean = false;
  isWishlisted: boolean = false;
  workshopSessions: WorkshopSessionDTO[] = [new WorkshopSessionDTO()];
  host: HostProfileDTO = new HostProfileDTO();
  rsvp: any;
  eventSubscriptions: MinorDTO[] = [];
  eventPicsIds: number[] = [];
  wishlist: any;
  eventPics: PictureDTO[] = [];
  eventPicsUrl: PictureDTO[] = [];
  createdByHostId: number;
  availableSlots: number;
  eventBadge: BadgeDTO;
  canJoin: boolean = false;
  isWaitlisted: boolean = false;
  alreadyReviewed: boolean = false;
  ambassadorReferralCode: string = "";
  ambassadosAllowed: number = 0;
  ambassadorOneCode: string = "";
  ambassadorTwoCode: string = "";
  coreConcepts: any[] = [];

  constructor(data: Partial<EventDTO> = {}) {
    for (let key in data) {
      if (key === "workshopSessions") {
        this[key] = data[key].map((i) => new WorkshopSessionDTO(i));
        if (this[key].length) {
          this.canJoin = isFuture(parseISO(this[key][0].startDateTime));
        }
      } else if (key === "host") {
        this[key] = new HostProfileDTO(data[key] ?? {});
      } else if (key === "eventPics") {
        this[key] = (data[key] ?? []).map((i) => new PictureDTO(i));
        // Also populate eventPicsUrl if eventPics is provided
        if (!this.eventPicsUrl || this.eventPicsUrl.length === 0) {
          this.eventPicsUrl = this[key];
        }
      } else if (key === "eventPicsUrl") {
        this[key] = (data[key] ?? []).map((i) => new PictureDTO(i));
      } else if (key === "coreConcepts") {
        // Handle coreConcepts array - extract IDs and store in coreConceptIds
        this.coreConceptIds = (data[key] ?? []).map((concept: any) => concept.id);
        this[key] = data[key] ?? [];
      } else if (Array.isArray(data[key])) {
        this[key] = data[key] ?? [];
      } else if (key === "ageGroup") {
        this[key] = data[key] as AGE_GROUPS;
      } else if (key === "skillLevel") {
        this[key] = data[key] as SKILL_LEVELS;
      } else if (key === "badge" || key === "eventBadge") {
        this[key] = new BadgeDTO(data[key] ?? {});
      } else {
        this[key] = data[key];
      }
    }
  }
}


function replaceUnderscores(str: string): string {
  return str.replaceAll("_", " ").toLowerCase();
}
export class WorkshopSessionDTO {
  id: number;
  startDateTime: string = "";
  endDateTime: string = "";
  endTime: string = "";
  eventId: number;
  deleted: boolean = false;
  year: number;
  startDate: string = "";
  startTime: string = "";
  startDateWithMonthAndYear: string = "";
  endDateWithMonthAndYear: string = "";
  month: string = "";
  rsvp: UserDTO = new UserDTO();

  constructor(data: Partial<WorkshopSessionDTO> = {}) {
    for (let key in data) {
      if (key === "startDateTime") {
        this.startDate = new Date(data[key]).getDate().toString();
        this[key] = format(parseISO(data[key]), "yyyy-MM-dd'T'HH:mm");
        this.startDateWithMonthAndYear = format(
          new Date(data[key]),
          "dd MMMM, yyyy"
        );
        this.startTime = format(new Date(data[key]), "hh:mm a");
        this.year = new Date(data[key]).getFullYear();
        this.month = format(new Date(data[key]), "MMM").toUpperCase();
      } else if (key === "endDateTime") {
        this.endDateWithMonthAndYear = format(
          new Date(data[key]),
          "dd MMMM, yyyy"
        );
        this.endTime = format(new Date(data[key]), "hh:mm a");
        this[key] = format(parseISO(data[key]), "yyyy-MM-dd'T'HH:mm");
      } else {
        this[key] = data[key];
      }
    }
  }
}

export class CreateEventPayloadDTO {
  eventId: number = 0;
  title: string = "";
  description: string = "";
  location: string = "";
  ageGroup: AGE_GROUPS;
  skillLevel: SKILL_LEVELS;
  coreConceptIds: number[] = [];
  joineeSlots: number = 0;
  parentSupervision: boolean = true;
  language: LANGUAGES;
  physicalConsideration: string = "";
  specialInstructions: string = "";
  badgeUrlId: number = 0;
  rsvpEmail: string = "";
  eventPicsIds: number[] = [];
  ambassadorSupportRequired: boolean = false;
  entryFee: number = 0;
  isSession: boolean = true;
  workshopSessions: {
    startDateTime: string;
    endDateTime: string;
  }[] = [];
  ambassadosAllowed: number = 0;
  ambassadorOneCode: string = "";
  ambassadorTwoCode: string = "";
  ambassadorReferralCode: string = "";

  constructor(data: Partial<CreateEventPayloadDTO> = {}) {
    Object.assign(this, data);
  }
}